import { z } from 'zod';
import { DateOnlySchema } from './common.js';

/**
 * Internal audits: an audit over the leaf clauses of selected top-level
 * clauses of a standard version, desk-audited clause by clause by the AI
 * auditor (findings always reviewed by people), with a report. Tables
 * internal_audits / internal_audit_items / internal_audit_findings /
 * internal_audit_ai_calls (migration 0016); routes in
 * apps/worker/src/routes/internal-audits.ts.
 */

export const INTERNAL_AUDIT_STATUSES = ['planned', 'in_progress', 'completed', 'approved'] as const;
export const InternalAuditStatusSchema = z.enum(INTERNAL_AUDIT_STATUSES);
export type InternalAuditStatus = z.infer<typeof InternalAuditStatusSchema>;
export const INTERNAL_AUDIT_STATUS_LABELS: Record<InternalAuditStatus, string> = {
  planned: 'Planned',
  in_progress: 'In progress',
  completed: 'Completed',
  approved: 'Approved',
};

export const AUDIT_ITEM_STATUSES = ['pending', 'running', 'done', 'failed', 'skipped'] as const;
export const AuditItemStatusSchema = z.enum(AUDIT_ITEM_STATUSES);
export type AuditItemStatus = z.infer<typeof AuditItemStatusSchema>;
export const AUDIT_ITEM_STATUS_LABELS: Record<AuditItemStatus, string> = {
  pending: 'Pending',
  running: 'Running',
  done: 'Done',
  failed: 'Failed',
  skipped: 'Skipped',
};

export const CONFORMITY = [
  'not_audited',
  'conforming',
  'minor_nonconformity',
  'major_nonconformity',
  'observation',
  'opportunity_for_improvement',
  'not_evidenced',
] as const;
export const ConformitySchema = z.enum(CONFORMITY);
export type Conformity = z.infer<typeof ConformitySchema>;
export const CONFORMITY_LABELS: Record<Conformity, string> = {
  not_audited: 'Not audited',
  conforming: 'Conforming',
  minor_nonconformity: 'Minor nonconformity',
  major_nonconformity: 'Major nonconformity',
  observation: 'Observation',
  opportunity_for_improvement: 'Opportunity for improvement',
  not_evidenced: 'Not evidenced',
};

export const FINDING_CLASSIFICATIONS = [
  'major_nonconformity',
  'minor_nonconformity',
  'observation',
  'opportunity_for_improvement',
  'conforming',
] as const;
export const FindingClassificationSchema = z.enum(FINDING_CLASSIFICATIONS);
export type FindingClassification = z.infer<typeof FindingClassificationSchema>;
export const FINDING_CLASSIFICATION_LABELS: Record<FindingClassification, string> = {
  major_nonconformity: 'Major nonconformity',
  minor_nonconformity: 'Minor nonconformity',
  observation: 'Observation',
  opportunity_for_improvement: 'Opportunity for improvement',
  conforming: 'Conforming',
};
/** Short definitions from the internal audit procedure (§5), shown in the UI and prompt. */
export const FINDING_CLASSIFICATION_DEFINITIONS: Record<FindingClassification, string> = {
  major_nonconformity:
    'The absence or total breakdown of a required control, or a systemic failure likely to result in a requirement not being fulfilled.',
  minor_nonconformity:
    'An isolated lapse against a requirement that does not undermine the management system.',
  observation:
    'A situation that is not yet a nonconformity but could become one if it is not addressed.',
  opportunity_for_improvement: 'A suggestion that goes beyond the requirements of the standard.',
  conforming: 'Objective evidence shows that the requirement is met.',
};

export const FINDING_STATUSES = ['open', 'accepted', 'rejected', 'actioned', 'closed'] as const;
export const FindingStatusSchema = z.enum(FINDING_STATUSES);
export type FindingStatus = z.infer<typeof FindingStatusSchema>;
export const FINDING_STATUS_LABELS: Record<FindingStatus, string> = {
  open: 'Open',
  accepted: 'Accepted',
  rejected: 'Rejected',
  actioned: 'Actioned',
  closed: 'Closed',
};

export const InternalAuditProgressSchema = z.object({
  total: z.number().int(),
  done: z.number().int(),
  failed: z.number().int(),
  pending: z.number().int(),
  running: z.number().int(),
  skipped: z.number().int(),
});
export type InternalAuditProgress = z.infer<typeof InternalAuditProgressSchema>;

export const InternalAuditCountsSchema = z.object({
  major: z.number().int(),
  minor: z.number().int(),
  observation: z.number().int(),
  ofi: z.number().int(),
  conforming: z.number().int(),
});
export type InternalAuditCounts = z.infer<typeof InternalAuditCountsSchema>;

export const InternalAuditScopeClauseSchema = z.object({
  clauseId: z.string(),
  number: z.string(),
  title: z.string(),
});
export type InternalAuditScopeClause = z.infer<typeof InternalAuditScopeClauseSchema>;

export const InternalAuditSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  standardVersionId: z.string(),
  standardName: z.string(),
  versionLabel: z.string(),
  assessmentId: z.string().nullable(),
  assessmentName: z.string().nullable(),
  name: z.string(),
  /** Ids of the selected top-level clauses. */
  scopeClauseIds: z.array(z.string()),
  scope: z.array(InternalAuditScopeClauseSchema),
  objectives: z.string(),
  criteria: z.string(),
  leadAuditorId: z.string().nullable(),
  leadAuditorName: z.string().nullable(),
  auditees: z.string(),
  /** Auditor independence: constraints and who second-reviews (procedure §3). */
  independenceNote: z.string(),
  plannedOn: z.string().nullable(),
  status: InternalAuditStatusSchema,
  conclusion: z.string(),
  includeEvidenceText: z.boolean(),
  approvedById: z.string().nullable(),
  approvedByName: z.string().nullable(),
  approvedAt: z.string().nullable(),
  createdBy: z.string(),
  createdByName: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  startedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  progress: InternalAuditProgressSchema,
  counts: InternalAuditCountsSchema,
});
export type InternalAudit = z.infer<typeof InternalAuditSchema>;

/** What the AI auditor examined for one clause (file names carry suffixes such as "(not read)"). */
export const InternalAuditExaminedSchema = z.object({
  responses: z.array(
    z.object({ responseId: z.string(), questionText: z.string(), status: z.string() }),
  ),
  evidence: z.array(z.string()),
  documents: z.array(z.string()),
  risks: z.array(z.string()),
});
export type InternalAuditExamined = z.infer<typeof InternalAuditExaminedSchema>;

export const InternalAuditFindingSchema = z.object({
  id: z.string(),
  auditId: z.string(),
  itemId: z.string(),
  clauseNumber: z.string(),
  classification: FindingClassificationSchema,
  title: z.string(),
  finding: z.string(),
  evidence: z.string(),
  recommendation: z.string(),
  status: FindingStatusSchema,
  decisionNote: z.string(),
  auditeeResponse: z.string(),
  aiGenerated: z.boolean(),
  correctiveActionRecordId: z.string().nullable(),
  /** '(record deleted)' when the pointer no longer resolves. */
  correctiveActionName: z.string().nullable(),
  createdByName: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type InternalAuditFinding = z.infer<typeof InternalAuditFindingSchema>;

export const InternalAuditItemSchema = z.object({
  id: z.string(),
  auditId: z.string(),
  clauseId: z.string(),
  clauseNumber: z.string(),
  clauseTitle: z.string(),
  sortOrder: z.number().int(),
  status: AuditItemStatusSchema,
  conformity: ConformitySchema,
  aiModel: z.string().nullable(),
  aiSummary: z.string(),
  examined: InternalAuditExaminedSchema,
  error: z.string().nullable(),
  auditorNote: z.string(),
  runAt: z.string().nullable(),
  findings: z.array(InternalAuditFindingSchema),
});
export type InternalAuditItem = z.infer<typeof InternalAuditItemSchema>;

const AUDIT_TEXT_MAX = 5000;
const FINDING_TITLE_MAX = 200;
const FINDING_TEXT_MAX = 2000;

export const CreateInternalAuditInputSchema = z.object({
  name: z.string().min(1).max(200),
  standardVersionId: z.string().min(1),
  /** An id or null — never an empty string, which would reach the FK column. */
  assessmentId: z.string().min(1).nullable().optional(),
  /** Ids of top-level clauses of that version (validated server-side). */
  scopeClauseIds: z.array(z.string().min(1)).min(1).max(50),
  objectives: z.string().max(AUDIT_TEXT_MAX).optional(),
  criteria: z.string().max(AUDIT_TEXT_MAX).optional(),
  leadAuditorId: z.string().min(1).nullable().optional(),
  auditees: z.string().max(AUDIT_TEXT_MAX).optional(),
  independenceNote: z.string().max(2000).optional(),
  plannedOn: DateOnlySchema.optional(),
  includeEvidenceText: z.boolean().optional(),
});
export type CreateInternalAuditInput = z.infer<typeof CreateInternalAuditInputSchema>;

export const UpdateInternalAuditInputSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  objectives: z.string().max(AUDIT_TEXT_MAX).optional(),
  criteria: z.string().max(AUDIT_TEXT_MAX).optional(),
  leadAuditorId: z.string().min(1).nullable().optional(),
  auditees: z.string().max(AUDIT_TEXT_MAX).optional(),
  independenceNote: z.string().max(2000).optional(),
  plannedOn: DateOnlySchema.nullable().optional(),
  conclusion: z.string().max(AUDIT_TEXT_MAX).optional(),
  includeEvidenceText: z.boolean().optional(),
  assessmentId: z.string().min(1).nullable().optional(),
});
export type UpdateInternalAuditInput = z.infer<typeof UpdateInternalAuditInputSchema>;

export const RunInternalAuditInputSchema = z.object({
  /** Items to process in this call (the web loops until `complete`). */
  limit: z.number().int().min(1).max(5).default(3),
  /** Re-run one item (done, failed, skipped or stale running). */
  itemId: z.string().min(1).optional(),
});
export type RunInternalAuditInput = z.infer<typeof RunInternalAuditInputSchema>;

export const RunInternalAuditResultSchema = z.object({
  processed: z.array(
    z.object({
      itemId: z.string(),
      clauseNumber: z.string(),
      status: AuditItemStatusSchema,
      conformity: ConformitySchema,
      findingCount: z.number().int(),
      error: z.string().nullable(),
    }),
  ),
  progress: InternalAuditProgressSchema,
  complete: z.boolean(),
});
export type RunInternalAuditResult = z.infer<typeof RunInternalAuditResultSchema>;

export const UpdateInternalAuditItemInputSchema = z.object({
  conformity: ConformitySchema.optional(),
  auditorNote: z.string().max(AUDIT_TEXT_MAX).optional(),
  /** 'pending' = manual reset (from running|failed|done|skipped); 'skipped' from any status except running. */
  status: z.enum(['skipped', 'pending']).optional(),
});
export type UpdateInternalAuditItemInput = z.infer<typeof UpdateInternalAuditItemInputSchema>;

export const CreateFindingInputSchema = z.object({
  itemId: z.string().min(1),
  classification: FindingClassificationSchema,
  title: z.string().min(1).max(FINDING_TITLE_MAX),
  finding: z.string().max(FINDING_TEXT_MAX).optional(),
  evidence: z.string().max(FINDING_TEXT_MAX).optional(),
  recommendation: z.string().max(FINDING_TEXT_MAX).optional(),
});
export type CreateFindingInput = z.infer<typeof CreateFindingInputSchema>;

export const UpdateFindingInputSchema = z.object({
  classification: FindingClassificationSchema.optional(),
  title: z.string().min(1).max(FINDING_TITLE_MAX).optional(),
  finding: z.string().max(FINDING_TEXT_MAX).optional(),
  evidence: z.string().max(FINDING_TEXT_MAX).optional(),
  recommendation: z.string().max(FINDING_TEXT_MAX).optional(),
  /** Whether action will be taken and why (procedure §7). */
  decisionNote: z.string().max(FINDING_TEXT_MAX).optional(),
  auditeeResponse: z.string().max(FINDING_TEXT_MAX).optional(),
  status: FindingStatusSchema.optional(),
});
export type UpdateFindingInput = z.infer<typeof UpdateFindingInputSchema>;

/** Body of POST /internal-audits/:auditId/complete — the audit conclusion (procedure §6). */
export const CompleteInternalAuditInputSchema = z.object({
  conclusion: z.string().min(1).max(AUDIT_TEXT_MAX),
});
export type CompleteInternalAuditInput = z.infer<typeof CompleteInternalAuditInputSchema>;

export const RaiseCorrectiveActionInputSchema = z.object({
  /** Must be an org member when set; defaults to the lead auditor while still a member. */
  ownerUserId: z.string().min(1).nullable().optional(),
  dueDate: DateOnlySchema.optional(),
});
export type RaiseCorrectiveActionInput = z.infer<typeof RaiseCorrectiveActionInputSchema>;
